import { Timestamp } from 'firebase/firestore';
import React from 'react';
import { CellProps, Column, Row, useExpanded, useFilters, useTable } from 'react-table';
import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import { NotificationRule, NotifSeverity } from '../../../sp-types/notificationRule';
import { Button, Checkbox, MenuItem, Select } from '@mui/material';
import { onOffChips } from './NotifRulesUtils';
import { ChatBubbleOutline, ExpandLess, ExpandMore, Language, PanTool, WarningAmberRounded } from '@mui/icons-material';
import { NotifExpandRow } from './NotifExpandRow';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../../components/Stores/accountStore';

export const dayPlural = (n: number) => `${n} Day${n === 1 ? '' : 's'}`;
const fmtDate = (ts: Timestamp) => ts?.toDate().toLocaleDateString('en-CA');

const GlobeIcon = () => (
  <span className="inline-flex h-5 w-5 items-center justify-center border-gray-300">
    <Language />
  </span>
);
export const SeverityIcon: React.FC<{ severity: NotifSeverity | string }> = ({ severity }) =>
  severity === 'warning' ? <WarningAmberRounded /> : severity === 'operation' ? <PanTool /> : <ChatBubbleOutline />;

type Props = {
  data: NotificationRule[];
  onChange?: (_updated: NotificationRule) => void;
  users?: any[];
  onEditRow?: (row: NotificationRule) => void;
};

export const NotificationsTable: React.FC<Props> = ({ data, onChange, users, onEditRow }) => {
  const [rowsState, setRowsState] = React.useState<NotificationRule[]>(() => data);
  const { materialsMap } = useMaterialsStore();
  const { materialColorDictionary: colorsMap } = useAccountStore();
  React.useEffect(() => setRowsState(data), [data]);

  const updateRow = (id: string, patch: Partial<NotificationRule>, row?: Row<NotificationRule>) => {
    if (row && (patch.enabled === false || patch.assignee === 'All')) {
      row.toggleRowExpanded(false);
    }
    setRowsState((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const next = rowsState.find((r) => r.id === id);
    if (next && onChange) onChange({ ...next, ...patch });
  };

  const columns = React.useMemo<Column<NotificationRule>[]>(
    () => [
      {
        Header: 'Rule',
        width: 100,
        maxWidth: 100,
        accessor: (row) => row.materialId,
        Cell: ({ value }: CellProps<NotificationRule>) => (
          <div className="flex items-center gap-2">
            <GlobeIcon />
            <span className="text-sm text-gray-800">{materialsMap[value]?.name || 'General'}</span>
          </div>
        ),
      },
      {
        Header: 'Active',
        accessor: 'enabled' as const,
        Cell: ({ row, value }: CellProps<NotificationRule>) => (
          <div className="flex items-center text-blue-600 ">
            <ToggleChips
              selected={value ? 1 : 0}
              setSelected={(isAct: number) => updateRow(row.original.id, { enabled: isAct === 1 }, row)}
              chips={onOffChips}
            />
          </div>
        ),
      },
      {
        Header: 'Duration',
        accessor: (row) => {
          // Prefer expireHours if provided; fallback to sendEveryDays
          if (row.expireHours && row.expireHours >= 24) {
            return dayPlural(Math.round(row.expireHours / 24));
          }
          if (row.sendEveryDays && row.sendEveryDays > 0) {
            return dayPlural(row.sendEveryDays);
          }
          return '—';
        },
      },
      // Type
      {
        Header: 'Type',
        accessor: (row) => row.severity,
        Cell: ({ value, row }: CellProps<NotificationRule>) => (
          <div className="flex items-center gap-2">
            <SeverityIcon severity={row.original.severity} />
            <span className="text-sm text-gray-800">{value}</span>
          </div>
        ),
      },
      // Description
      {
        Header: 'Description',
        accessor: (row) => row.name || row.condition,
        Cell: ({ row, value }: CellProps<NotificationRule>) => (
          <a className="text-sm text-blue-600 underline cursor pointer" onClick={() => onEditRow?.(row.original)}>
            {value}
          </a>
        ),
      },
      // Assignee
      {
        id: 'assignee',
        Header: 'Assignee',
        accessor: (row) => row.assignee || 'All',
        Cell: ({ row, value }: CellProps<NotificationRule>) => (
          <Select
            disabled={!row.original.enabled}
            className="w-32 h-8 text-sm focus:outline-none"
            value={value}
            onChange={({ target: { value: assignee } }) => updateRow(row.original.id, { assignee }, row)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Custom">Custom</MenuItem>
          </Select>
        ),
      },
      {
        Header: 'Dashboard',
        id: 'dashboard',
        accessor: (row) => !!row.options?.dashboard,
        Cell: () => <Checkbox checked disabled onChange={() => {}} />,
      },
      // WhatsApp
      {
        Header: 'WhatsApp',
        id: 'whatsapp',
        type: 'boolean',
        accessor: (row) => !!row.options?.whatsapp,
        Cell: ({ row, value }: CellProps<NotificationRule>) => (
          <Checkbox
            disabled={!row.original.enabled}
            checked={!!value}
            onChange={({ target }) =>
              updateRow(row.original.id, {
                options: { ...(row.original.options || {}), whatsapp: !!target.checked },
              })
            }
          />
        ),
      },
      // Email
      {
        Header: 'Email',
        id: 'email',
        type: 'boolean',
        accessor: (row) => !!row.options?.email,
        Cell: ({ row, value }: CellProps<NotificationRule>) => (
          <Checkbox
            checked={!!value}
            disabled={!row.original.enabled}
            onChange={({ target }) =>
              updateRow(row.original.id, {
                options: { ...(row.original.options || {}), email: !!target.checked },
              })
            }
          />
        ),
      },
      {
        id: 'modifiedAt',
        Header: 'Modify at',
        accessor: (row) => fmtDate(row.created_at),
      },
      {
        Header: 'Expand',
        accessor: (row) => row.id,
        Cell: ({ row }: CellProps<NotificationRule>) =>
          row.values.assignee === 'Custom' && row.values.enabled ? (
            <Button
              className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
              onClick={() => {
                row.toggleRowExpanded();
                console.log('row', row, row.original.assignees);
              }}
              title="expand"
            >
              {row.isExpanded ? <ExpandLess /> : <ExpandMore />}
            </Button>
          ) : (
            <ExpandMore className="text-gray-350 ml-5" />
          ),
      },
    ],
    [rowsState],
  );

  const table = useTable<NotificationRule>(
    { columns, data: rowsState, autoResetExpanded: false },
    useFilters,
    useExpanded,
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns } = table;

  return (
    <div className="w-full overflow-x-auto">
      <table {...getTableProps()} className="min-w-[900px] w-full border-separate border-spacing-y-[0.4rem]">
        <thead>
          {headerGroups.map((hg, i) => (
            <tr key={i}>
              {hg.headers.map((col, j) => (
                <th
                  key={j}
                  className="bg-gray-50 text-gray-600 font-normal text-sm text-left py-1 px-3 border-gray-125 border-l border-y last:border-r"
                >
                  {col.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.map((row: Row<NotificationRule>) => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id}>
                <tr {...row.getRowProps()} key={row.id} className={row.original.enabled ? 'bg-white' : 'bg-gray-50'}>
                  {row.cells.map((cell, k) => (
                    <td
                      key={k}
                      className={`border-gray-125 first:border-l last:border-r py-2 px-4 whitespace-nowrap overflow-hidden text-ellipsis ${
                        row.isExpanded ? 'border-t' : 'border-y'
                      }`}
                      style={
                        k === 0
                          ? {
                              borderLeft:
                                row.original.materialId && colorsMap[row.original.materialId]
                                  ? `8px solid ${colorsMap[row.original.materialId]}`
                                  : '8px solid gray',
                            }
                          : {}
                      }
                    >
                      <div>{cell.render('Cell')}</div>
                    </td>
                  ))}
                </tr>
                {row.isExpanded && users && <NotifExpandRow columns={visibleColumns} {...{ row, users, updateRow }} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
